/**
 * v2 Settings → Jobs. List + per-job detail (results table +
 * transcript drawer, reusing the same in-page slide-over pattern
 * the Activity tab uses on chassis pages).
 */
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ChevronLeft, Terminal } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../../../api/client";
import { LocalTime } from "../../../lib/datetime";
import { Badge, type BadgeTone } from "../../components/Badge";
import { CopyableCell } from "../../components/CopyableCell";
import { FilterBar } from "../../components/FilterBar";
import { JobActions } from "../../components/JobActions";
import { TranscriptLines } from "../../components/TranscriptLines";
import { HelpButton } from "../../help/HelpPanel";

type Job = {
  id: string;
  chassis_id: number | null;
  rendered_name: string;
  scope: string;
  status: string;
  started_by_gigafy_username: string;
  started_at: string | null;
  completed_at: string | null;
  total_targets: number | null;
  completed_targets: number | null;
  // v0.14.1 joined denorm from /api/jobs (LEFT JOIN chassis +
  // gigafy_locations_cache). May be null if chassis was deleted
  // or the location cache is cold.
  gigafy_device_name: string | null;
  gigafy_location_id: string | null;
  gigafy_location_name: string | null;
};

type JobsResponse = { jobs: Job[] };

const STATUS_TONE: Record<string, BadgeTone> = {
  queued: "neutral",
  "queued-on-chassis": "neutral",
  running: "info",
  succeeded: "success",
  failed: "danger",
  cancelled: "muted",
  partial: "warning",
};

// A per-target result counts as a failure unless it succeeded outright or is
// a benign "no_macs" / "skipped" outcome (collect_mac against an ONU with no active
// subscriber, or whose async MAC dump never returned a table). Keeps a few
// empty ONUs out of the failure count / errors-only view.
function isFailureStatus(status: string): boolean {
  return status !== "success" && status !== "no_macs" && status !== "skipped";
}

// v0.34.0 — automation split. Operator (default) shows human-started
// work only; Automation shows the background rows (auto-provision
// cycles + their batches) the operator list deliberately hides.
type JobsView = "operator" | "automation";

export function Jobs() {
  const [view, setView] = useState<JobsView>("operator");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [filter, setFilter] = useState("");
  const q = useQuery<JobsResponse>({
    queryKey: ["v2", "jobs", view, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: "100",
        background: String(view === "automation"),
      });
      if (statusFilter) params.set("status", statusFilter);
      const res = await api.get<JobsResponse>(`/jobs?${params.toString()}`);
      return res.data;
    },
    refetchInterval: 5_000,
  });
  // Client-side substring filter over the fetched page (job name,
  // chassis, location, user, status, id) — the shared FilterBar.
  const needle = filter.trim().toLowerCase();
  const jobs = useMemo(() => {
    const all = q.data?.jobs ?? [];
    if (!needle) return all;
    return all.filter((j) =>
      [
        j.rendered_name,
        j.status,
        j.id,
        j.gigafy_device_name,
        j.gigafy_location_name,
        j.started_by_gigafy_username,
      ].some((v) => (v ?? "").toLowerCase().includes(needle)),
    );
  }, [q.data, needle]);
  return (
    <section className="flex flex-col gap-3">
      <header>
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-slate-100">Jobs</h1>
          <HelpButton topic="settings-jobs" />
        </div>
      </header>
      <div className="flex items-center gap-2 text-xs">
        <div className="inline-flex overflow-hidden rounded border border-slate-700">
          {(["operator", "automation"] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={[
                "px-2 py-1 capitalize",
                v === view
                  ? "bg-emerald-900/40 text-emerald-200"
                  : "text-slate-300 hover:bg-slate-800",
              ].join(" ")}
            >
              {v}
            </button>
          ))}
        </div>
        <span className="h-4 w-px bg-slate-700" />
        {["", "queued", "running", "succeeded", "failed", "partial"].map((s) => (
          <button
            key={s || "all"}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={[
              "rounded border px-2 py-1",
              s === statusFilter
                ? "border-emerald-700 bg-emerald-900/40 text-emerald-200"
                : "border-slate-700 text-slate-300 hover:bg-slate-800",
            ].join(" ")}
          >
            {s || "all"}
          </button>
        ))}
        <FilterBar
          value={filter}
          onChange={setFilter}
          placeholder="Filter by job, chassis, user…"
          className="ml-auto w-72"
        />
      </div>
      <div className="rounded-lg bg-slate-900 shadow-border p-4">
        {q.isLoading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : jobs.length === 0 ? (
          <p className="text-xs text-slate-500">No jobs match.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1">Status</th>
                <th className="px-2 py-1">Job</th>
                <th className="px-2 py-1">Scope</th>
                <th className="px-2 py-1">Chassis</th>
                <th className="px-2 py-1">Location</th>
                <th className="px-2 py-1">By</th>
                <th className="px-2 py-1">Progress</th>
                <th className="px-2 py-1">Started</th>
                <th className="px-2 py-1 text-right">Open</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/40 text-slate-300">
              {jobs.map((j) => (
                <tr key={j.id}>
                  <td className="px-2 py-1.5">
                    <Badge tone={STATUS_TONE[j.status] ?? "neutral"}>
                      {j.status}
                    </Badge>
                  </td>
                  <td className="px-2 py-1.5 font-mono">{j.rendered_name}</td>
                  <td className="px-2 py-1.5">{j.scope}</td>
                  <td className="px-2 py-1.5">
                    {j.chassis_id ? (
                      <Link
                        to={`/chassis/${j.chassis_id}`}
                        className="text-emerald-400 hover:text-emerald-300"
                      >
                        {j.gigafy_device_name ?? `chassis-${j.chassis_id}`}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-[11px]">
                    {j.gigafy_location_id && j.gigafy_location_name ? (
                      <Link
                        to={`/locations/${j.gigafy_location_id}`}
                        className="text-slate-300 hover:text-slate-100"
                      >
                        {j.gigafy_location_name}
                      </Link>
                    ) : j.gigafy_location_id ? (
                      <span className="text-slate-500" title={j.gigafy_location_id}>
                        (cache cold)
                      </span>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">{j.started_by_gigafy_username}</td>
                  <td className="px-2 py-1.5 font-mono text-[11px]">
                    {j.total_targets
                      ? `${j.completed_targets ?? 0}/${j.total_targets}`
                      : "—"}
                  </td>
                  <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">
                    <LocalTime iso={j.started_at} />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <span className="inline-flex items-center justify-end gap-2">
                      <JobActions job={j} />
                      <Link
                        to={`/settings/jobs/${j.id}`}
                        className="text-emerald-400 hover:text-emerald-300"
                      >
                        open
                      </Link>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

export function JobDetail({ jobId }: { jobId: string }) {
  type Results = {
    job: Job;
    results: {
      id: number;
      target: string;
      status: string;
      raw_output: string | null;
      parsed_data: Record<string, unknown> | null;
      executed_at: string;
    }[];
  };
  type TranscriptResp = {
    rows: {
      id: number;
      ts: string;
      direction: string;
      content: string;
      byte_len: number | null;
      truncated: boolean;
    }[];
  };
  const results = useQuery<Results>({
    queryKey: ["v2", "jobs", jobId, "results"],
    queryFn: async () => {
      const res = await api.get<Results>(`/jobs/${jobId}/results`);
      return res.data;
    },
    refetchInterval: 3_000,
  });
  const transcript = useQuery<TranscriptResp>({
    queryKey: ["v2", "jobs", jobId, "transcript"],
    queryFn: async () => {
      const res = await api.get<TranscriptResp>(
        `/transcripts?job_id=${jobId}&limit=500`,
      );
      return res.data;
    },
    refetchInterval: 3_000,
  });
  const job = results.data?.job;
  const allResults = results.data?.results ?? [];
  const errorCount = useMemo(
    () => allResults.filter((r) => isFailureStatus(r.status)).length,
    [allResults],
  );
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);
  // Display set: errors-only filter ON shows only failures; OFF caps at 200 rows so
  // huge jobs still render quickly. Operator can flip the filter to drill into failures.
  const visibleResults = useMemo(() => {
    if (showOnlyErrors) {
      return allResults.filter((r) => isFailureStatus(r.status));
    }
    return allResults.slice(0, 200);
  }, [allResults, showOnlyErrors]);
  // Worker-log command pre-filled with the job's start timestamp so the
  // operator can correlate stderr with the failed targets.
  const workerLogCmd = job?.started_at
    ? `sudo docker compose -f /opt/chassis/ops/docker-compose.yml logs worker --since ${job.started_at.replace(/\.\d+Z$/, "Z")}`
    : "sudo docker compose -f /opt/chassis/ops/docker-compose.yml logs worker --tail 200";
  return (
    <section className="flex flex-col gap-3">
      <Link
        to="/settings/jobs"
        className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200"
      >
        <ChevronLeft className="h-3 w-3" />
        Back to jobs
      </Link>
      <header className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-lg font-semibold text-slate-100">
          {job?.rendered_name ?? "job"}
        </h1>
        <HelpButton topic="settings-jobs" />
        {job && (
          <Badge tone={STATUS_TONE[job.status] ?? "neutral"}>{job.status}</Badge>
        )}
        <CopyableCell value={jobId} />
        {job && <JobActions job={job} />}
      </header>

      {/* Error summary banner — only when at least one target failed. */}
      {errorCount > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-red-800 bg-red-900/30 px-3 py-2 text-xs text-red-200">
          <div className="inline-flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              <strong>{errorCount}</strong>
              {" of "}
              <strong>{allResults.length}</strong>
              {" target"}{allResults.length === 1 ? "" : "s"}
              {" failed."}
              {job?.status === "partial"
                ? " Job marked partial — see per-row diagnostics below."
                : null}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setShowOnlyErrors((v) => !v)}
            className="rounded border border-red-700 bg-red-950/50 px-2 py-1 text-[11px] text-red-100 hover:bg-red-900/60"
          >
            {showOnlyErrors ? "Show all rows" : "Show only failures"}
          </button>
        </div>
      ) : null}

      <section className="rounded-lg bg-slate-900 shadow-border p-4">
        <h2 className="mb-2 text-sm font-medium text-slate-200">
          Per-target results ({visibleResults.length}
          {!showOnlyErrors && allResults.length > visibleResults.length
            ? ` of ${allResults.length}`
            : ""}
          )
        </h2>
        {results.isLoading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : allResults.length === 0 ? (
          <p className="text-xs text-slate-500">
            No results yet
            {job?.status && ["queued", "queued-on-chassis"].includes(job.status)
              ? " — job hasn't started."
              : "."}
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1">Target</th>
                <th className="px-2 py-1">Status</th>
                <th className="px-2 py-1">Executed</th>
                <th className="px-2 py-1">Raw / parsed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/40 text-slate-300">
              {visibleResults.map((r) => {
                const isError = isFailureStatus(r.status);
                // "no_macs" is a non-error outcome (ONU with no active
                // subscriber / dump never returned a table) — amber, not red.
                // "skipped" = services a MAC-sweep batch left for the next run
                // (time budget) — also amber.
                const isNoMacs = r.status === "no_macs" || r.status === "skipped";
                // Pull a one-line error message for inline preview — prefer
                // parsed_data.error_message (from v0.14.0 dispatcher catch) then
                // fall back to the first line of raw_output.
                const errMsg = isError
                  ? (typeof r.parsed_data?.error_message === "string"
                      ? r.parsed_data.error_message
                      : r.raw_output?.split("\n")[0]) ?? null
                  : null;
                return (
                  <Fragment key={r.id}>
                    <tr className={isError ? "bg-red-950/20" : ""}>
                      <td className="px-2 py-1 font-mono">
                        <CopyableCell value={r.target} />
                      </td>
                      <td className="px-2 py-1">
                        <Badge
                          tone={
                            isError ? "danger" : isNoMacs ? "warning" : "success"
                          }
                        >
                          {r.status}
                        </Badge>
                      </td>
                      <td className="px-2 py-1 font-mono text-[10px] text-slate-500">
                        <LocalTime iso={r.executed_at} />
                      </td>
                      <td className="px-2 py-1 text-[10px]">
                        {r.parsed_data ? (
                          <details>
                            <summary className="cursor-pointer text-slate-400">
                              parsed
                            </summary>
                            <pre className="mt-1 overflow-x-auto rounded bg-slate-950 p-1.5 font-mono text-[10px] text-slate-400">
                              {JSON.stringify(r.parsed_data, null, 2)}
                            </pre>
                          </details>
                        ) : null}
                        {r.raw_output ? (
                          <details>
                            <summary className="cursor-pointer text-slate-400">
                              raw ({r.raw_output.length} bytes)
                            </summary>
                            <pre className="mt-1 overflow-x-auto rounded bg-slate-950 p-1.5 font-mono text-[10px] text-slate-400">
                              {r.raw_output.slice(0, 8000)}
                            </pre>
                          </details>
                        ) : null}
                        {!r.parsed_data && !r.raw_output && (
                          <span className="text-slate-600">
                            {isError
                              ? "no diagnostic captured — check worker logs"
                              : "—"}
                          </span>
                        )}
                      </td>
                    </tr>
                    {isError && errMsg ? (
                      <tr className="bg-red-950/20">
                        <td colSpan={4} className="px-2 pb-1.5 text-[11px] text-red-200">
                          <span className="font-mono">↳ {errMsg}</span>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {/* Worker-logs hint — pre-filled command keyed to job's start time. */}
      {errorCount > 0 ? (
        <section className="rounded-lg bg-slate-900 shadow-border p-4 text-xs text-slate-300">
          <h2 className="mb-2 inline-flex items-center gap-2 text-sm font-medium text-slate-200">
            <Terminal className="h-4 w-4" />
            Inspect worker logs
          </h2>
          <p className="mb-2 text-slate-400">
            Run on the host to see structlog lines from the worker covering
            this job's runtime:
          </p>
          <pre className="overflow-x-auto rounded bg-slate-950 p-2 font-mono text-[11px] text-slate-200">
            {workerLogCmd}
          </pre>
        </section>
      ) : null}

      <section className="rounded-lg bg-slate-900 shadow-border p-4">
        <h2 className="mb-2 text-sm font-medium text-slate-200">
          Transcript ({transcript.data?.rows.length ?? 0} rows)
        </h2>
        {transcript.isLoading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : (
          <TranscriptLines rows={transcript.data?.rows ?? []} />
        )}
      </section>
    </section>
  );
}
